/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type MouseEvent } from 'react';
import cx from 'classnames';
import root from 'window-or-global';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { historyStateOptionAttributes } from '@/constants/historyStateOption';
import { replaceHistoryStateOptionsOnClick } from '@/features/common/utils/replaceHistoryStateOptionsOnClick';
import { appendSearchTextToUrl } from '@/utils/urlHelpers';
import { SmartLink as Link } from '@/utils/smartLink';
import NextImage, { akamaiLoader } from '@/components/NextImage';
import { sessionStorage } from '@/utils/sessionStorage';
import viewStyles from '../viewStyles.module.scss';
import styles from './styles.module.scss';
import { buildUrlWithAcesParams } from '@/utils/buildUrlWithAcesParams';

type ProductImageProps = {
  alt: string;
  src: string;
  url: string | undefined;
  prodIndex: number;
  partGroupId: string;
  brandId: string;
  positionId?: string;
  uniqueId?: number;
  isSearchPageType: boolean;
  onClick?: () => void;
  isSponsoredCard?: boolean;
};

export const ProductImage = ({
  alt,
  src,
  url,
  prodIndex,
  partGroupId,
  brandId,
  positionId,
  uniqueId,
  isSearchPageType,
  onClick,
  isSponsoredCard,
}: ProductImageProps) => {
  const router = useLegacyRouter();
  // Normalize string | string[] to string before passing to utility
  const searchTextQuery = Array.isArray(router.query.searchText)
    ? router.query.searchText[0]
    : router.query.searchText;
  const filterByKeyWordQuery = Array.isArray(router.query.filterByKeyWord)
    ? router.query.filterByKeyWord[0]
    : router.query.filterByKeyWord;

  const updatedUrl = appendSearchTextToUrl(url, searchTextQuery, filterByKeyWordQuery);

  const urlWithAcesParams = buildUrlWithAcesParams(updatedUrl, {
    partGroupId,
    brandId,
    positionId,
    uniqueId,
  });

  const onImageClick = () => {
    if (isSearchPageType) {
      sessionStorage.setItem(
        'productRank',
        isSponsoredCard ? 'Recommended' : String(prodIndex + 1),
      );
    }
    onClick?.();
    replaceHistoryStateOptionsOnClick(
      historyStateOptionAttributes.latestSelectedPageYOffset,
      root.scrollY || root.pageYOffset,
    );
  };

  const onAuxClick = (e: MouseEvent<HTMLImageElement>) => {
    if (e.button === 1) {
      onClick?.();
    }
  };

  return (
    <div data-testid="product-image-container" className={styles.productImageContainer}>
      <Link
        samePage
        className={`${styles.productImageLink} ${viewStyles.productImage}`}
        to={urlWithAcesParams}
        maskedURL={updatedUrl}
      >
        <NextImage
          priority={prodIndex === 0}
          src={src}
          alt={alt}
          height={180}
          width={180}
          data-testid="shelf-product-image"
          className={cx(styles.image, styles.responsiveImage)}
          onClick={onImageClick}
          onAuxClick={onAuxClick}
          loader={akamaiLoader}
          unoptimized={false}
          sizes="(max-width: 719px) 64px, (max-width: 1139px) 96px, 128px"
        />
      </Link>
    </div>
  );
};
